// API key scopes — the one list both the key-creation form (as checkboxes) and
// POST /api/orgs/:orgId/keys (as validation) use, so the UI can only offer a
// scope the server will accept and enforce.
//
// What each scope gates today:
//   read   nothing extra: every valid key may query (MCP, /api/context, search,
//          GET /api/ci/refs). Recorded so a key says what it is for.
//   write  the CI routes (server/lc/middleware.ts): /api/ci/config hands back
//          the org's decrypted LLM key, /api/ci/index and /api/ci/file-cache
//          write into the org. Only org admins may grant it.
//
// `search` and `admin` are accepted for keys created before this list existed
// (and by API callers that still send them); `admin` satisfies every scope
// check, so it is privileged like `write`.

export interface ApiKeyScopeOption {
  scope: 'read' | 'write';
  label: string;
  description: string;
  /** Always granted; shown checked and locked. */
  always?: boolean;
  /** Only org owners and admins may grant it. */
  adminOnly?: boolean;
}

export const API_KEY_SCOPE_OPTIONS: readonly ApiKeyScopeOption[] = [
  {
    scope: 'read',
    label: 'Read',
    description: 'Query packages and private repositories: MCP, /api/context, search.',
    always: true,
  },
  {
    scope: 'write',
    label: 'Write (CI)',
    description:
      "Index from CI: push packages and fetch the org's CI config, including its LLM key. Give it only to a CI secret.",
    adminOnly: true,
  },
];

const LEGACY_SCOPES = ['search', 'admin'];
const ACCEPTED = new Set<string>([...API_KEY_SCOPE_OPTIONS.map((o) => o.scope), ...LEGACY_SCOPES]);
const PRIVILEGED = new Set<string>(['write', 'admin']);

export class ApiKeyScopeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiKeyScopeError';
  }
}

/**
 * The scopes to store for a new key: `read` always, plus the requested ones,
 * deduplicated in a stable order. Unknown scopes are rejected rather than
 * stored — a typo like `wrte` would otherwise mint a key that silently cannot
 * do what it was made for.
 */
export function normalizeApiKeyScopes(requested: unknown): string[] {
  if (requested === undefined || requested === null) return ['read'];
  if (!Array.isArray(requested) || !requested.every((s) => typeof s === 'string')) {
    throw new ApiKeyScopeError('scopes must be an array of strings.');
  }
  const scopes = new Set(['read', ...requested.map((s) => s.trim()).filter(Boolean)]);
  const unknown = [...scopes].filter((s) => !ACCEPTED.has(s));
  if (unknown.length > 0) {
    throw new ApiKeyScopeError(
      `Unknown scope${unknown.length > 1 ? 's' : ''}: ${unknown.join(', ')}. ` +
        `Valid: ${API_KEY_SCOPE_OPTIONS.map((o) => o.scope).join(', ')}.`
    );
  }
  return [...ACCEPTED].filter((s) => scopes.has(s));
}

/** Whether granting these scopes needs an org owner or admin. */
export function requiresOrgAdmin(scopes: string[]): boolean {
  return scopes.some((s) => PRIVILEGED.has(s));
}
